/**
 * Format an ISO date or timestamp into IST string
 */
export function fmtDate(iso, options = {}) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;

    const defaults = {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };

    return new Intl.DateTimeFormat('en-IN', { ...defaults, ...options }).format(d);
  } catch (e) {
    return iso;
  }
}

/**
 * Format an ISO timestamp or HH:MM into IST 12-hour time (e.g. 08:15 AM)
 */
export function fmtTime(iso) {
  if (!iso) return '—';
  try {
    // If it's already HH:MM or HH:MM:SS
    if (typeof iso === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(iso)) {
      const [h, m] = iso.split(':');
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${String(formattedHour).padStart(2, '0')}:${m} ${ampm}`;
    }

    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(d);
  } catch (e) {
    return iso;
  }
}

/**
 * Format an integer or float amount as Indian Rupee (₹)
 */
export function money(amount) {
  if (amount === undefined || amount === null) return '₹0';
  const num = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(num);
}

/**
 * Format a decimal or ratio as percentage (e.g., 87.5%)
 */
export function pct(num, digits = 1) {
  if (num === undefined || num === null) return '0%';
  const val = typeof num === 'number' ? num : parseFloat(num) || 0;
  return `${val.toFixed(digits)}%`;
}

/**
 * Extract 1 or 2 uppercase initials from a name
 */
export function initials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Aliases for intuitive naming
export const formatDate = fmtDate;
export const formatTime = fmtTime;
export const formatCurrency = money;
